import React, { useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteLayout from "../components/layout/SiteLayout";
import AppIcon from "../components/AppIcon";
import { APPS } from "../data/apps";
import { AppFilter, filterApps, whereClauses } from "../lib/appQuery";
import { Platform } from "../types/app";
import "../styles/apps.css";

const PAGE_SIZE = 10;
const PLATFORMS: (Platform | "*")[] = ["*", "ios", "android", "web"];
const CATEGORIES = ["*", ...Array.from(new Set(APPS.map((a) => a.category)))];
const dot = (d: string) => d.slice(0, 7).replace("-", ".");

export default function AppsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "만든 앱 | kwangbok.me";
  }, []);

  // D-07: 필터와 검색 상태는 URL 쿼리에 둔다. 모르는 값은 전체(*)로 본다.
  const rawPlatform = params.get("platform") as Platform | null;
  const rawCategory = params.get("category");
  const filter: AppFilter = {
    q: params.get("q") ?? "",
    platform: rawPlatform && PLATFORMS.includes(rawPlatform) ? rawPlatform : "*",
    category: rawCategory && CATEGORIES.includes(rawCategory) ? rawCategory : "*",
  };
  const rows = filterApps(APPS, filter);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const page = Math.min(pages, Math.max(1, Number(params.get("page")) || 1));
  const offset = (page - 1) * PAGE_SIZE;
  const where = whereClauses(filter);

  // 값이 기본값이면 쿼리에서 지운다. 조건이 바뀌면 1페이지로.
  const update = (key: "q" | "platform" | "category" | "page", value: string, replace = false) => {
    const next = new URLSearchParams(params);
    if (!value || value === "*" || (key === "page" && value === "1")) next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setParams(next, { replace });
  };

  return (
    <SiteLayout crumb="apps">
      <div className="apps">
        <div className="apps-head">
          <div className="apps-title">
            <h1>만든 앱</h1>
            <p className="muted">앱마다 상세, 개인정보처리방침, 고객지원 페이지가 있습니다.</p>
          </div>
          <label className="apps-search mono">
            <span className="muted">search:</span>
            <input
              type="search"
              placeholder="앱 이름, 설명"
              value={filter.q}
              onChange={(e) => update("q", e.target.value, true)}
            />
          </label>
        </div>

        <p className="apps-query mono">
          <span className="apps-kw">SELECT</span> name, category, platform, status <span className="apps-kw">FROM</span> app
          {where.length > 0 && (
            <>
              {" "}
              <span className="apps-kw">WHERE</span> {where.join(" AND ")}
            </>
          )}{" "}
          <span className="apps-kw">ORDER BY</span> released_at <span className="apps-kw">DESC</span>;
        </p>

        <div className="apps-filters">
          <div className="apps-chips" role="group" aria-label="platform 필터">
            <span className="mono muted apps-chips-label">platform</span>
            {PLATFORMS.map((p) => (
              <button key={p} type="button" className="chip" aria-pressed={filter.platform === p} onClick={() => update("platform", p)}>
                {p}
              </button>
            ))}
          </div>
          <div className="apps-chips" role="group" aria-label="category 필터">
            <span className="mono muted apps-chips-label">category</span>
            {CATEGORIES.map((c) => (
              <button key={c} type="button" className="chip" aria-pressed={filter.category === c} onClick={() => update("category", c)}>
                {c}
              </button>
            ))}
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="card apps-empty">
            {APPS.length === 0 ? (
              <p>준비 중인 앱이 있습니다.</p>
            ) : (
              <>
                <p>조건에 맞는 앱이 없습니다.</p>
                <button type="button" className="chip" onClick={() => setParams(new URLSearchParams())}>
                  조건 초기화
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="card apps-table-wrap">
            <table className="apps-table">
              <thead>
                <tr>
                  <th scope="col">name</th>
                  <th scope="col">category</th>
                  <th scope="col">platform</th>
                  <th scope="col">status</th>
                  <th scope="col">released_at</th>
                  <th scope="col">links</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(offset, offset + PAGE_SIZE).map((a) => (
                  <tr
                    key={a.slug}
                    className={a.pinned ? "apps-row is-pinned" : "apps-row"}
                    // 행 어디를 눌러도 상세로. 키보드는 이름 링크로 같은 곳에 간다.
                    onClick={(e) => {
                      if (!(e.target as HTMLElement).closest("a, button")) navigate(`/apps/${a.slug}`);
                    }}
                  >
                    <td className="apps-c-name">
                      <Link to={`/apps/${a.slug}`} className="apps-name-row">
                        <AppIcon app={a} className="apps-icon" />
                        <span className="apps-name">
                          <strong>{a.name}</strong>
                          <span className="muted">{a.summary}</span>
                        </span>
                      </Link>
                    </td>
                    <td className="apps-c-cat muted">
                      <button
                        type="button"
                        className="apps-cat-btn"
                        aria-label={`${a.category} 카테고리만 보기`}
                        onClick={() => update("category", a.category)}
                      >
                        {a.category}
                      </button>
                    </td>
                    <td className="apps-c-plat mono">{a.platforms.join(", ")}</td>
                    <td className="apps-c-status">
                      <span className={`tag tag--${a.status}`}>{a.status}</span>
                    </td>
                    <td className="apps-c-date mono muted">
                      <time dateTime={a.released_at}>{dot(a.released_at)}</time>
                    </td>
                    <td className="apps-c-links">
                      <Link to={`/apps/${a.slug}`} aria-label={`${a.name} 상세`}>상세</Link>
                      <Link to={`/apps/${a.slug}/privacy`} aria-label={`${a.name} 개인정보처리방침`}>정책</Link>
                      <Link to={`/apps/${a.slug}/support`} aria-label={`${a.name} 고객지원`}>지원</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="apps-pager mono">
          <span aria-live="polite">
            {rows.length} rows<span className="apps-limit">, LIMIT {PAGE_SIZE} OFFSET {offset}</span>
          </span>
          <nav aria-label="페이지">
            {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                className="chip"
                aria-current={n === page ? "page" : undefined}
                aria-label={`${n}페이지`}
                onClick={() => update("page", String(n))}
              >
                {n}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </SiteLayout>
  );
}
